import { useCallback, useEffect, useMemo, useState } from 'react';
import { chevronPoints, linePathD, lineZone } from '@urbanrats/domain';
import { can } from '@urbanrats/ops-core';
import { Icon } from '../components/Icon.tsx';
import { LineEditor, type EditLine } from '../components/LineEditor.tsx';
import { WorldMap } from '../components/WorldMap.tsx';
import { fmtTime, useCtx } from '../lib.ts';

interface Row {
  camera_id: string;
  name: string | null;
  city: string | null;
  road: string | null;
  country: string | null;
  country_code: string | null;
  latitude: number | null;
  longitude: number | null;
  resolution: string | null;
  fps: number | null;
  state: string;
  provider_id: string;
  has_stream: boolean;
  active_calibration_version: number | null;
  hold_until: string | null;
  hold_reason: string | null;
  health_status: string | null;
}
interface Country {
  code: string;
  name: string | null;
  n: number;
  streams: number;
  pool: number;
}
interface Version {
  version: number;
  status: string;
  note: string;
  createdBy: string;
  createdAt: string;
  line: EditLine & { lineId: string };
  crossingRule: string;
  hasReference: boolean;
}
interface Job {
  job_id: string;
  kind: string;
  state: string;
  stage: string | null;
  log: { at: string; msg: string }[];
  result: { ok?: boolean; stage?: string; reason?: string | null } | null;
  created_by: string;
  created_at: string;
}
interface Detail {
  camera: Row & { rights_status: string; warm: boolean; last_error: string | null; state_reason: string | null; score: number | null };
  active: { version: number } | null;
  versions: Version[];
  rounds: { game_round_id: string; state: string; final_count: number | null; created_at: string; last_reason: string | null }[];
  jobs: Job[];
}

const PAGE = 60;
const flag = (cc: string | null) => (cc && /^[A-Z]{2}$/.test(cc) ? String.fromCodePoint(...[...cc].map((c) => 0x1f1e6 + c.charCodeAt(0) - 65)) : '🌐');
const short = (id: string) => id.replace(/^argus:opencctv_/, '').replace(/^[^_]+_/, '');

/** Search the whole camera catalogue, look at any camera, add it to the pool and draw its counting line. */
export function Studio() {
  const { backend, user, search } = useCtx();
  const server = backend.server;
  const manage = can(user, 'camera.manage');
  const [q, setQ] = useState('');
  const [country, setCountry] = useState('');
  const [state, setState] = useState('');
  const [offset, setOffset] = useState(0);
  const [rows, setRows] = useState<{ total: number; cameras: Row[] } | null>(null);
  const [countries, setCountries] = useState<Country[]>([]);
  const [sel, setSel] = useState<string | null>(() => new URLSearchParams(search).get('camera'));
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    if (!server) return;
    const p = new URLSearchParams({ stream: '1', limit: String(PAGE), offset: String(offset) });
    if (q.trim()) p.set('search', q.trim());
    if (country) p.set('country', country);
    if (state) p.set('state', state);
    const r = await server.get<{ total: number; cameras: Row[] }>(`/v1/admin/cameras?${p}`);
    if (r.ok) setRows(r.value);
    else setError(r.error);
  }, [server, q, country, state, offset]);
  useEffect(() => {
    const t = setTimeout(() => void load(), 250);
    return () => clearTimeout(t);
  }, [load]);
  useEffect(() => {
    const t = setTimeout(() => void server?.get<{ countries: Country[] }>('/v1/admin/cameras-countries').then((r) => r.ok && setCountries(r.value.countries)), 0);
    return () => clearTimeout(t);
  }, [server]);
  if (!server) return <p className="empty">The camera studio needs the UrbanRats backend.</p>;
  const points = (rows?.cameras ?? [])
    .filter((c) => c.latitude !== null && c.longitude !== null)
    .map((c) => ({ id: c.camera_id, lon: c.longitude!, lat: c.latitude!, kind: c.camera_id === sel ? 'on-air' : c.state === 'ACTIVE' ? 'ready' : c.state === 'STANDBY' ? 'standby' : 'warm', title: `${c.city ?? ''} ${c.country ?? ''} · ${c.name ?? short(c.camera_id)}` }));

  return (
    <div className="studio" data-testid="studio">
      <div className="cc-head">
        <div>
          <h1>Camera studio</h1>
          <p className="note">Search every camera we know of, look at its picture, add it to the pool and draw its counting line. Cameras without provider rights stay internal (testing only).</p>
        </div>
        <div className="cc-head-tags">
          <span className="pill">{rows ? `${rows.total.toLocaleString('en-US')} cameras with video` : '…'}</span>
        </div>
      </div>
      <div className="studio-grid">
        <section className="card-x studio-list">
          <div className="studio-filters">
            <label className="search-field">
              <Icon name="search" size={16} />
              <input value={q} onChange={(e) => (setQ(e.target.value), setOffset(0))} placeholder="City, road, name or id…" aria-label="Search cameras" data-testid="studio-search" />
            </label>
            <select value={country} onChange={(e) => (setCountry(e.target.value), setOffset(0))} aria-label="Country">
              <option value="">All countries</option>
              {countries
                .filter((c) => c.streams > 0)
                .map((c) => (
                  <option key={c.code} value={c.code}>
                    {flag(c.code)} {c.name ?? c.code} · {c.streams}
                  </option>
                ))}
            </select>
            <select value={state} onChange={(e) => (setState(e.target.value), setOffset(0))} aria-label="Pool state">
              <option value="">Any state</option>
              <option value="ACTIVE">In the pool (active)</option>
              <option value="STANDBY">Standby</option>
              <option value="QUALIFIED">Qualified</option>
              <option value="DISCOVERED">Not checked yet</option>
            </select>
          </div>
          {points.length > 0 && <WorldMap points={points} focus={sel} onPick={(id) => setSel(id)} />}
          {error && <p className="warn-box">{error}</p>}
          <ul className="cam-list" data-testid="studio-results">
            {rows?.cameras.map((c) => (
              <li key={c.camera_id} className={c.camera_id === sel ? 'on' : ''}>
                <button type="button" onClick={() => setSel(c.camera_id)}>
                  <span className="flag" aria-hidden>
                    {flag(c.country_code)}
                  </span>
                  <span className="where">
                    <strong>{c.name ?? short(c.camera_id)}</strong>
                    <span className="muted">
                      {[c.city, c.country].filter(Boolean).join(', ') || '—'} · {c.provider_id.replace(/^opencctv_/, '')}
                    </span>
                  </span>
                  <span className="cam-tags">
                    {c.state === 'ACTIVE' && <span className="tag st-ACTIVE">pool</span>}
                    {c.active_calibration_version && <span className="tag st-INFO">line v{c.active_calibration_version}</span>}
                    {c.resolution && <span className="muted small">{c.resolution}</span>}
                  </span>
                </button>
              </li>
            ))}
            {rows?.cameras.length === 0 && <li className="muted">No camera matches.</li>}
          </ul>
          {rows && rows.total > PAGE && (
            <div className="pager">
              <button type="button" className="btn ghost" disabled={offset === 0} onClick={() => setOffset(Math.max(0, offset - PAGE))}>
                ← Previous
              </button>
              <span className="note">
                {offset + 1}–{Math.min(rows.total, offset + PAGE)} of {rows.total.toLocaleString('en-US')}
              </span>
              <button type="button" className="btn ghost" disabled={offset + PAGE >= rows.total} onClick={() => setOffset(offset + PAGE)}>
                Next →
              </button>
            </div>
          )}
        </section>
        {sel ? <CameraPanel key={sel} id={sel} manage={manage} onChanged={() => void load()} /> : <section className="card-x studio-empty"><p className="muted">Pick a camera from the list or the map.</p></section>}
      </div>
    </div>
  );
}

function CameraPanel({ id, manage, onChanged }: { id: string; manage: boolean; onChanged: () => void }) {
  const { backend } = useCtx();
  const server = backend.server!;
  const [d, setD] = useState<Detail | null>(null);
  const [img, setImg] = useState<{ url: string; source: string | null; ratio: number; at: string } | null>(null);
  const [clip, setClip] = useState<string | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const [msg, setMsg] = useState<{ ok: boolean; text: string } | null>(null);
  const [edit, setEdit] = useState<EditLine | null>(null);
  const [reason, setReason] = useState('');
  const loadDetail = useCallback(async () => {
    const r = await server.get<Detail>(`/v1/admin/cameras/${encodeURIComponent(id)}/studio`);
    if (r.ok) setD(r.value);
    else setMsg({ ok: false, text: r.error });
  }, [server, id]);
  const loadImage = useCallback(
    async (fresh = false) => {
      setBusy('picture');
      const r = await server.blob(`/v1/admin/cameras/${encodeURIComponent(id)}/preview.jpg${fresh ? '?fresh=1' : ''}`);
      setBusy(null);
      if (!r.ok) return setMsg({ ok: false, text: r.error });
      const el = new Image();
      el.onload = () =>
        setImg((old) => {
          if (old) URL.revokeObjectURL(old.url);
          return { url: r.value.url, source: r.value.source, ratio: el.naturalWidth / el.naturalHeight || 16 / 9, at: new Date().toISOString() };
        });
      el.src = r.value.url;
    },
    [server, id],
  );
  useEffect(() => {
    const t = setTimeout(() => {
      void loadDetail();
      void loadImage();
    }, 0);
    return () => clearTimeout(t);
  }, [loadDetail, loadImage]);
  // While a job runs, follow its progress.
  const running = d?.jobs.some((j) => j.state === 'RUNNING');
  useEffect(() => {
    if (!running) return;
    const t = setInterval(() => void loadDetail(), 3000);
    return () => clearInterval(t);
  }, [running, loadDetail]);
  const active = useMemo(() => (d?.active ? (d.versions.find((v) => v.version === d.active!.version) ?? null) : null), [d]);
  if (!d) return <section className="card-x">{msg ? <p className="warn-box">{msg.text}</p> : <p className="muted">Loading camera…</p>}</section>;
  const c = d.camera;
  const startEdit = () => {
    setEdit(active ? { a: active.line.a, b: active.line.b, curve: active.line.curve, countedDirection: active.line.countedDirection, ...(active.line.zone ? { zone: active.line.zone } : {}) } : { a: { x: 0.3, y: 0.62 }, b: { x: 0.7, y: 0.62 }, countedDirection: 'A_TO_B' });
    setMsg(null);
  };
  const onboard = async (line: 'auto' | 'manual') => {
    const why = prompt(`Add ${short(id)} to the pool (${line === 'auto' ? 'automatic line' : 'you draw the line'}).\n\nReason (audit, at least 10 characters):`);
    if (!why || why.trim().length < 10) return;
    setBusy('onboard');
    const r = await server.post<{ jobId: string }>(`/v1/admin/cameras/${encodeURIComponent(id)}/onboard`, { line, reason: why.trim() });
    setBusy(null);
    setMsg(r.ok ? { ok: true, text: `Onboarding started (${r.value.jobId}): health → stability → activation${line === 'auto' ? ' → automatic line → trial window' : ''}. About ${line === 'auto' ? '5' : '2'} minutes.` } : { ok: false, text: r.error });
    void loadDetail();
  };
  const save = async () => {
    if (!edit) return;
    setBusy('save');
    const r = await server.post<{ version: number }>(`/v1/admin/cameras/${encodeURIComponent(id)}/line`, { line: edit, reason: reason.trim() });
    setBusy(null);
    if (!r.ok) return setMsg({ ok: false, text: r.error });
    setMsg({ ok: true, text: `Line saved as calibration v${r.value.version} (PROVISIONAL, accuracy not measured). The live counter uses it from its next session.` });
    setEdit(null);
    setReason('');
    void loadDetail();
    onChanged();
  };
  const job = d.jobs[0];

  return (
    <section className="card-x cam-panel" data-testid="studio-camera">
      <header className="card-h">
        <span className="flag" aria-hidden>
          {flag(c.country_code)}
        </span>
        <div className="where">
          <strong>{c.name ?? short(c.camera_id)}</strong>
          <span className="muted">
            {[c.road, c.city, c.country].filter(Boolean).join(' · ')} · {c.provider_id}
          </span>
        </div>
        <span className="spacer" />
        <span className={`tag st-${c.state}`}>{c.state}</span>
        <span className={`tag ${c.rights_status === 'APPROVED' ? 'st-OK' : 'st-PENDING'}`} title="Provider rights: player-facing rounds need APPROVED">
          rights {c.rights_status}
        </span>
      </header>

      {edit && img ? (
        <>
          <LineEditor image={img.url} ratio={img.ratio} value={edit} saved={active ? active.line : null} onChange={setEdit} disabled={busy === 'save'} />
          <div className="le-tools">
            <label className="toggle-x">
              <input
                type="checkbox"
                checked={!!edit.curve}
                onChange={(e) => {
                  const n = { x: -(edit.b.y - edit.a.y), y: edit.b.x - edit.a.x };
                  const m = { x: (edit.a.x + edit.b.x) / 2, y: (edit.a.y + edit.b.y) / 2 };
                  setEdit(e.target.checked ? { ...edit, curve: { x: Math.min(1, Math.max(0, m.x + n.x * 0.25)), y: Math.min(1, Math.max(0, m.y + n.y * 0.25)) } } : { a: edit.a, b: edit.b, countedDirection: edit.countedDirection, ...(edit.zone ? { zone: edit.zone } : {}) });
                }}
                data-testid="le-curve"
              />
              Curved line
            </label>
            <button type="button" className="btn ghost" onClick={() => setEdit({ a: edit.a, b: edit.b, ...(edit.curve ? { curve: edit.curve } : {}), countedDirection: edit.countedDirection === 'A_TO_B' ? 'B_TO_A' : 'A_TO_B' })} data-testid="le-flip">
              ⇄ Flip counted direction
            </button>
            <button type="button" className="btn ghost" onClick={() => setEdit({ a: edit.a, b: edit.b, ...(edit.curve ? { curve: edit.curve } : {}), countedDirection: edit.countedDirection })} disabled={!edit.zone} data-testid="le-zone-reset">
              Automatic zone
            </button>
            <button type="button" className="btn ghost" onClick={() => void loadImage(true)} disabled={!!busy}>
              New picture
            </button>
            <span className="note">
              Drag A, B{edit.curve ? ' and the bend point' : ''} for the counting line; drag A′ and B′ along the lanes to shape the yellow zone players see. Arrow keys nudge a focused handle (Shift = more). Only vehicles crossing the line in the chevrons’ direction count — the zone is a visual guide.
            </span>
          </div>
          <div className="le-save">
            <input value={reason} onChange={(e) => setReason(e.target.value)} placeholder="Reason (audit): e.g. line moved to the middle of the junction" aria-label="Reason" data-testid="le-reason" />
            <button type="button" className="btn primary" disabled={!!busy || reason.trim().length < 10} onClick={() => void save()} data-testid="le-save">
              {busy === 'save' ? 'Saving…' : 'Save line'}
            </button>
            <button type="button" className="btn ghost" onClick={() => setEdit(null)}>
              Cancel
            </button>
          </div>
        </>
      ) : (
        <div className="cam-media" style={{ aspectRatio: `${img?.ratio ?? 16 / 9}` }}>
          {clip ? <video src={clip} autoPlay muted playsInline controls onEnded={() => setClip(null)} /> : img ? <img src={img.url} alt={`Picture of ${c.name ?? id}`} /> : <div className="relay-msg">{busy === 'picture' ? 'Fetching the picture…' : 'No picture'}</div>}
          {img && !clip && (
            <span className="relay-behind">
              {img.source === 'buffer' ? 'from our buffer' : 'live from the camera'} · {new Date(img.at).toISOString().slice(11, 19)} UTC
            </span>
          )}
          {active && img && !clip && (
            <svg className="cam-line" viewBox={`0 0 1000 ${Math.round(1000 / img.ratio)}`} aria-hidden>
              <LinePreview line={active.line} h={Math.round(1000 / img.ratio)} />
            </svg>
          )}
        </div>
      )}

      {!edit && (
        <div className="cam-actions">
          <button type="button" className="btn" onClick={() => void loadImage(true)} disabled={!!busy}>
            <Icon name="camera" size={16} className="inline-icon" /> {busy === 'picture' ? 'Fetching…' : 'Refresh picture'}
          </button>
          <button
            type="button"
            className="btn"
            disabled={!!busy}
            onClick={async () => {
              setBusy('clip');
              const r = await server.blob(`/v1/admin/cameras/${encodeURIComponent(id)}/clip.mp4`);
              setBusy(null);
              if (r.ok) setClip(r.value.url);
              else setMsg({ ok: false, text: r.error });
            }}
            data-testid="studio-clip"
          >
            ▶ {busy === 'clip' ? 'Loading…' : 'Play ~10 s'}
          </button>
          {manage && c.state === 'ACTIVE' && (
            <button type="button" className="btn primary" onClick={startEdit} disabled={!img} data-testid="studio-edit-line">
              {active ? 'Edit counting line' : 'Draw counting line'}
            </button>
          )}
          {manage && c.state !== 'ACTIVE' && c.state !== 'DISABLED' && !running && (
            <>
              <button type="button" className="btn primary" onClick={() => void onboard('manual')} disabled={!!busy} data-testid="studio-add-manual">
                Add to pool · I draw the line
              </button>
              <button type="button" className="btn" onClick={() => void onboard('auto')} disabled={!!busy}>
                Add to pool · automatic line
              </button>
            </>
          )}
        </div>
      )}
      {msg && <p className={msg.ok ? 'ok-box' : 'warn-box'}>{msg.text}</p>}

      {job && (
        <div className={`job job-${job.state}`} data-testid="studio-job">
          <strong>
            {job.kind === 'ONBOARD_MANUAL' ? 'Adding to the pool (line by hand)' : 'Adding to the pool (automatic line)'} · {job.state}
            {job.stage ? ` · ${job.stage}` : ''}
          </strong>
          {job.result?.reason && <span className="muted"> — {job.result.reason}</span>}
          <ol className="job-log">
            {job.log.slice(-6).map((l, i) => (
              <li key={i}>
                <span className="muted">{l.at.slice(11, 19)}</span> {l.msg.replace(id, '').replace(/^:\s*/, '')}
              </li>
            ))}
          </ol>
        </div>
      )}

      <div className="cam-facts">
        <dl className="kv">
          <dt>Picture</dt>
          <dd>
            {c.resolution ?? '—'}
            {c.fps ? ` · ${c.fps} fps` : ''}
          </dd>
          <dt>Health</dt>
          <dd>
            {c.health_status ?? '—'}
            {c.last_error ? ` · ${c.last_error}` : ''}
          </dd>
          {c.hold_until && new Date(c.hold_until) > new Date() && (
            <>
              <dt>Held until</dt>
              <dd>
                {fmtTime(c.hold_until)} — {c.hold_reason}
              </dd>
            </>
          )}
          <dt>Counting line</dt>
          <dd>{active ? `v${active.version} · ${active.status} · ${active.crossingRule}${active.line.curve ? ' · curved' : ''} · by ${active.createdBy}` : 'none'}</dd>
        </dl>
        {d.versions.length > 1 && (
          <details>
            <summary>Line history ({d.versions.length})</summary>
            <ul className="plain small-list">
              {d.versions.map((v) => (
                <li key={v.version}>
                  v{v.version} · {fmtTime(v.createdAt)} · {v.createdBy} · {v.note}
                </li>
              ))}
            </ul>
          </details>
        )}
        {d.rounds.length > 0 && (
          <details>
            <summary>Recent rounds ({d.rounds.length})</summary>
            <ul className="plain small-list">
              {d.rounds.map((r) => (
                <li key={r.game_round_id}>
                  {r.game_round_id} · {r.state} · {r.final_count ?? '—'} {r.last_reason ? `· ${r.last_reason}` : ''}
                </li>
              ))}
            </ul>
          </details>
        )}
      </div>
    </section>
  );
}

/** The active line as players see it: approach zone, chevrons in the counted direction, the bold line. */
function LinePreview({ line, h }: { line: EditLine; h: number }) {
  const W = 1000;
  const d = linePathD(line, W, h);
  const z = lineZone(line, W, h);
  const zonePts = `${z.near.a.x * W},${z.near.a.y * h} ${z.near.b.x * W},${z.near.b.y * h} ${line.b.x * W},${line.b.y * h} ${line.a.x * W},${line.a.y * h}`;
  return (
    <>
      <polygon points={zonePts} className="cl-zone" />
      {z.chevrons.map((c, i) => (
        <polyline key={i} points={chevronPoints(c, W, h).map((p) => p.join(',')).join(' ')} className="cl-chev" />
      ))}
      <path d={d} className="le-casing" />
      <path d={d} className="le-line" />
    </>
  );
}
